import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Check, ArrowLeft, Sparkles, Percent, Clock, Building2, User, Mail, Phone, Users, Calendar } from "lucide-react";

const BACKEND_URL = ((typeof window !== 'undefined' && window.location.hostname.includes('seatpilot.co.uk')) ? 'https://seatpilot.co.uk' : (process.env.REACT_APP_BACKEND_URL || 'https://seatpilot.co.uk'));
const API = `${BACKEND_URL}/api`;

export default function PricingPage() {
  const navigate = useNavigate();
  const { user, subscription, isAuthenticated, login } = useAuth();
  const [loading, setLoading] = useState(null);
  const [billingPeriod, setBillingPeriod] = useState("monthly");
  const [showEnterpriseModal, setShowEnterpriseModal] = useState(false);
  const [enterpriseForm, setEnterpriseForm] = useState({
    company_name: "",
    contact_name: "",
    email: "",
    phone: "",
    expected_seats: "",
    expected_events: "",
    message: ""
  });
  const [submittingEnquiry, setSubmittingEnquiry] = useState(false);
  const [dynamicPlans, setDynamicPlans] = useState(null);

  // Fetch dynamic plans from backend
  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const res = await axios.get(`${API}/subscriptions/plans/public`);
        if (res.data.monthly && res.data.yearly) {
          setDynamicPlans(res.data);
        }
      } catch (error) {
        console.error("Failed to fetch plans:", error);
      }
    };
    fetchPlans();
  }, []);

  // Default plans as fallback
  const defaultPlans = {
    monthly: [
      {
        id: "free_trial",
        name: "Free Trial",
        price: "£0",
        period: "for 1 month",
        description: "Perfect for testing",
        features: [
          "1 event",
          "1 layout",
          "Up to 500 seats",
          "Basic support"
        ],
        disabled: subscription?.plan_id === "free_trial" || subscription?.status === "active"
      },
      {
        id: "pay_as_you_go",
        name: "Pay As You Go",
        price: "£0.50",
        period: "per seat",
        description: "For occasional events",
        features: [
          "Unlimited events",
          "Unlimited layouts",
          "Pay only for bookings",
          "No monthly fees",
          "Email support"
        ]
      },
      {
        id: "monthly",
        name: "Monthly",
        price: "£50",
        period: "per month",
        description: "For regular event organizers",
        features: [
          "Unlimited events",
          "Unlimited layouts",
          "10,000 seats/month included",
          "£0.30/seat after limit",
          "Priority support",
          "Analytics dashboard"
        ],
        popular: true
      },
      {
        id: "enterprise",
        name: "Enterprise",
        price: "Custom",
        period: "pricing",
        description: "For large-scale operations",
        features: [
          "Custom seat allowance",
          "Custom pricing per seat",
          "Dedicated support",
          "SLA guarantee",
          "White-label options",
          "Custom integrations"
        ],
        isEnterprise: true
      }
    ],
    yearly: [
      {
        id: "free_trial",
        name: "Free Trial",
        price: "£0",
        period: "for 1 month",
        description: "Perfect for testing",
        features: [
          "1 event",
          "1 layout",
          "Up to 500 seats",
          "Basic support"
        ],
        disabled: true,
        hideOnYearly: true
      },
      {
        id: "pay_as_you_go",
        name: "Pay As You Go",
        price: "£0.50",
        period: "per seat",
        description: "For occasional events",
        features: [
          "Unlimited events",
          "Unlimited layouts",
          "Pay only for bookings",
          "No monthly fees",
          "Email support"
        ]
      },
      {
        id: "monthly_yearly",
        name: "Annual",
        price: "£480",
        originalPrice: "£600",
        period: "per year",
        monthlyEquivalent: "£40/month",
        description: "Save 20% with annual billing",
        features: [
          "Everything in Monthly",
          "10,000 seats/month included",
          "£0.30/seat after limit",
          "Priority support",
          "Analytics dashboard",
          "14-day cooling-off period"
        ],
        popular: true,
        savings: "Save £120/year"
      },
      {
        id: "enterprise_yearly",
        name: "Enterprise Annual",
        price: "Custom",
        period: "pricing",
        description: "20% off annual enterprise",
        features: [
          "Custom seat allowance",
          "Custom pricing per seat",
          "Dedicated support",
          "SLA guarantee",
          "White-label options",
          "14-day cooling-off period"
        ],
        isEnterprise: true,
        savings: "20% off"
      }
    ]
  };

  // Use dynamic plans if available, otherwise fallback to defaults
  const plans = dynamicPlans ? {
    monthly: dynamicPlans.monthly.map(p => ({
      ...p,
      disabled: p.id === "free_trial" && (subscription?.plan_id === "free_trial" || subscription?.status === "active")
    })),
    yearly: dynamicPlans.yearly.map(p => ({
      ...p,
      disabled: p.id === "free_trial"
    }))
  } : defaultPlans;

  const handleSelectPlan = async (planId) => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    if (planId === "enterprise" || planId === "enterprise_yearly") {
      // Pre-fill form with user data if authenticated
      if (user) {
        setEnterpriseForm(prev => ({
          ...prev,
          contact_name: user.name || "",
          email: user.email || ""
        }));
      }
      setShowEnterpriseModal(true);
      return;
    }

    setLoading(planId);
    try {
      const response = await axios.post(
        `${API}/subscriptions/subscribe`,
        {
          plan_id: planId,
          origin_url: window.location.origin
        },
        { withCredentials: true }
      );

      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else if (response.data.success) {
        toast.success(response.data.message || "Plan activated!");
        navigate("/dashboard");
      }
    } catch (error) {
      if (error.response?.status === 400 && 
          error.response?.headers?.['x-requires-payment-method'] === 'true') {
        toast.info("Please add a card to use Pay As You Go plan");
        navigate("/dashboard?tab=billing&add_card=true&plan=pay_as_you_go");
        return;
      }
      toast.error(error.response?.data?.detail || "Failed to change plan");
    } finally {
      setLoading(null);
    }
  };

  const handleEnterpriseSubmit = async (e) => {
    e.preventDefault();
    
    if (!enterpriseForm.company_name || !enterpriseForm.contact_name || !enterpriseForm.email) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    setSubmittingEnquiry(true);
    try {
      const response = await axios.post(`${API}/support/enterprise-enquiry`, enterpriseForm);
      toast.success(response.data.message || "Enquiry submitted successfully!");
      setShowEnterpriseModal(false);
      setEnterpriseForm({
        company_name: "",
        contact_name: "",
        email: "",
        phone: "",
        expected_seats: "",
        expected_events: "",
        message: ""
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit enquiry");
    } finally {
      setSubmittingEnquiry(false);
    }
  };

  const currentPlans = plans[billingPeriod].filter(p => !p.hideOnYearly || billingPeriod === 'monthly');

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white py-12 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="mb-8">
          <Button 
            variant="ghost" 
            onClick={() => navigate(isAuthenticated ? '/dashboard' : '/')}
            className="gap-2"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </Button>
        </div>

        <div className="text-center mb-12">
          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">Choose Your Plan</h1>
          <p className="text-gray-600 max-w-2xl mx-auto mb-8">
            {subscription ? 
              `You're currently on the ${subscription.plan_id === 'free_trial' ? 'Free Trial' : subscription.plan_id} plan.` :
              'Start free and scale as you grow. All plans include our powerful seating chart builder.'
            }
          </p>
          
          {/* Billing Toggle */}
          <div className="inline-flex items-center bg-gray-100 rounded-full p-1 mb-8">
            <button
              onClick={() => setBillingPeriod('monthly')}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                billingPeriod === 'monthly' 
                  ? 'bg-white shadow text-gray-900' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingPeriod('yearly')}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all flex items-center gap-2 ${
                billingPeriod === 'yearly' 
                  ? 'bg-white shadow text-gray-900' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Yearly
              <span className="bg-green-100 text-green-700 text-xs px-2 py-0.5 rounded-full">
                Save 20%
              </span>
            </button>
          </div>
          
          {/* Yearly Benefits Banner */}
          {billingPeriod === 'yearly' && (
            <div className="max-w-2xl mx-auto mb-8 bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4">
              <div className="flex flex-wrap items-center justify-center gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <Percent className="w-5 h-5 text-green-600" />
                  <span className="text-green-800 font-medium">20% discount on all plans</span>
                </div>
                <div className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-green-600" />
                  <span className="text-green-800 font-medium">Pay once, use all year</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-green-600" />
                  <span className="text-green-800 font-medium">14-day money-back guarantee</span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {currentPlans.map((plan) => (
            <Card 
              key={plan.id}
              className={`p-6 relative ${plan.popular ? 'border-2 border-violet-500 shadow-xl scale-105' : 'hover:shadow-lg transition-shadow'}`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-violet-600 to-indigo-600 text-white text-xs font-medium px-3 py-1 rounded-full">
                  Most Popular
                </div>
              )}
              {plan.savings && (
                <div className="absolute -top-3 right-4 bg-green-500 text-white text-xs font-medium px-3 py-1 rounded-full flex items-center gap-1">
                  <Sparkles className="w-3 h-3" />
                  {plan.savings}
                </div>
              )}
              {subscription?.plan_id === plan.id && (
                <div className="absolute -top-3 right-4 bg-blue-500 text-white text-xs font-medium px-3 py-1 rounded-full">
                  Current Plan
                </div>
              )}
              <div className="text-center mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{plan.name}</h3>
                <div className="flex items-baseline justify-center gap-1">
                  {plan.originalPrice && (
                    <span className="text-gray-400 line-through text-lg mr-1">{plan.originalPrice}</span>
                  )}
                  <span className="text-4xl font-bold text-gray-900">{plan.price}</span>
                  <span className="text-gray-500 text-sm">{plan.period}</span>
                </div>
                {plan.monthlyEquivalent && (
                  <p className="text-green-600 text-sm font-medium mt-1">
                    That&apos;s just {plan.monthlyEquivalent}
                  </p>
                )}
                <p className="text-gray-500 text-sm mt-2">{plan.description}</p>
              </div>
              <ul className="space-y-3 mb-6">
                {plan.features.map((feature, fIndex) => (
                  <li key={fIndex} className="flex items-start gap-2 text-sm text-gray-600">
                    <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    {feature}
                  </li>
                ))}
              </ul>
              <Button
                className={`w-full ${plan.popular ? 'bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-700 hover:to-indigo-700' : ''}`}
                variant={plan.popular ? 'default' : 'outline'}
                onClick={() => handleSelectPlan(plan.id)}
                disabled={loading === plan.id || (plan.disabled && subscription?.plan_id === plan.id)}
              >
                {loading === plan.id ? "Processing..." : 
                 subscription?.plan_id === plan.id ? "Current Plan" :
                 plan.isEnterprise ? "Contact Sales" : "Get Started"}
              </Button>
            </Card>
          ))}
        </div>
        
        {/* Money-back guarantee */}
        <div className="text-center mt-12">
          <div className="inline-flex items-center gap-2 bg-gray-100 rounded-full px-4 py-2 text-sm text-gray-600">
            <Check className="w-4 h-4 text-green-500" />
            All paid plans include a 14-day cooling-off period with full refund
          </div>
        </div>
        
        {/* FAQ Section */}
        <div className="mt-16 max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 text-center mb-8">Frequently Asked Questions</h2>
          <div className="space-y-4">
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-2">Can I switch between plans?</h3>
              <p className="text-gray-600 text-sm">Yes! You can upgrade or downgrade at any time. When upgrading, you&apos;ll be charged the prorated difference. When downgrading, the change takes effect at your next billing cycle.</p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-2">What happens if I exceed my seat limit?</h3>
              <p className="text-gray-600 text-sm">On the Monthly plan, you&apos;ll be charged £0.30 per additional seat. On Pay As You Go, you pay £0.50 per seat with no monthly commitment.</p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-2">Is there a refund policy?</h3>
              <p className="text-gray-600 text-sm">Yes! All yearly plans come with a 14-day cooling-off period. Cancel within 14 days for a full refund, no questions asked.</p>
            </div>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <h3 className="font-semibold text-gray-900 mb-2">Do I need a credit card for the free trial?</h3>
              <p className="text-gray-600 text-sm">No! Start your free trial without entering any payment details. You&apos;ll only be asked to add a payment method when you choose to upgrade.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Enterprise Enquiry Modal */}
      <Dialog open={showEnterpriseModal} onOpenChange={setShowEnterpriseModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-violet-600" />
              Enterprise Plan Enquiry
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleEnterpriseSubmit} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Company Name *
                </label>
                <div className="relative">
                  <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    className="pl-10"
                    placeholder="Your company"
                    value={enterpriseForm.company_name}
                    onChange={(e) => setEnterpriseForm({ ...enterpriseForm, company_name: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contact Name *
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    className="pl-10"
                    placeholder="Your name"
                    value={enterpriseForm.contact_name}
                    onChange={(e) => setEnterpriseForm({ ...enterpriseForm, contact_name: e.target.value })}
                    required
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    type="email"
                    className="pl-10"
                    placeholder="you@company.com"
                    value={enterpriseForm.email}
                    onChange={(e) => setEnterpriseForm({ ...enterpriseForm, email: e.target.value })}
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone
                </label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    className="pl-10"
                    placeholder="+44 123 456 7890"
                    value={enterpriseForm.phone}
                    onChange={(e) => setEnterpriseForm({ ...enterpriseForm, phone: e.target.value })}
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Expected Seats/Month
                </label>
                <div className="relative">
                  <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <select
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                    value={enterpriseForm.expected_seats}
                    onChange={(e) => setEnterpriseForm({ ...enterpriseForm, expected_seats: e.target.value })}
                  >
                    <option value="">Select range</option>
                    <option value="10000-50000">10,000 - 50,000</option>
                    <option value="50000-100000">50,000 - 100,000</option>
                    <option value="100000-500000">100,000 - 500,000</option>
                    <option value="500000+">500,000+</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Expected Events/Month
                </label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <select
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                    value={enterpriseForm.expected_events}
                    onChange={(e) => setEnterpriseForm({ ...enterpriseForm, expected_events: e.target.value })}
                  >
                    <option value="">Select range</option>
                    <option value="1-10">1 - 10</option>
                    <option value="10-50">10 - 50</option>
                    <option value="50-100">50 - 100</option>
                    <option value="100+">100+</option>
                  </select>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tell us about your requirements
              </label>
              <textarea
                className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                rows={3}
                placeholder="Any specific features, integrations, or customizations you need..."
                value={enterpriseForm.message}
                onChange={(e) => setEnterpriseForm({ ...enterpriseForm, message: e.target.value })}
              />
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                className="flex-1"
                onClick={() => setShowEnterpriseModal(false)}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-gradient-to-r from-violet-600 to-indigo-600"
                disabled={submittingEnquiry}
              >
                {submittingEnquiry ? "Submitting..." : "Submit Enquiry"}
              </Button>
            </div>

            <p className="text-xs text-center text-gray-500">
              Our sales team will contact you within 24 hours
            </p>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
